import React from 'react';
import { API } from '../api';

/* ====================================================================
   CLUB PICKER — the front door.

   Somebody arriving at GreenSheet is arriving at their club, so the first
   thing they see is the clubs: a logo and a name each, and one tap to their
   own club's login. The choice is remembered, so this is a screen people
   see once rather than every visit.

   Only clubs listed publicly are here. The catch-all Default Club and any
   demo club are left out by the server.
   ==================================================================== */

// Where the choice is remembered. Read by AuthScreen too.
export const CHOSEN_CLUB_KEY = 'greensheet_club';

export function rememberClub(id) {
  try {
    if (id) localStorage.setItem(CHOSEN_CLUB_KEY, String(id));
    else localStorage.removeItem(CHOSEN_CLUB_KEY);
  } catch { /* a browser that will not store it just asks again next time */ }
}

export function rememberedClub() {
  try { return localStorage.getItem(CHOSEN_CLUB_KEY) || ''; } catch { return ''; }
}

export const clubLogoUrl = club => (club && club.hasLogo ? `${API}/club/${club.id}/logo.png` : null);

/* A club with no logo gets its initials, so the grid still reads as clubs
   and not as a list of names with holes in it. */
const initials = name => String(name || '')
  .split(/\s+/)
  .filter(w => w && !/^(bowling|bowls|club|inc|the|of|and|&)$/i.test(w))
  .slice(0, 2)
  .map(w => w[0].toUpperCase())
  .join('') || '?';

function ClubTile({ club, onPick }) {
  const [broken, setBroken] = React.useState(false);
  const logo = !broken && clubLogoUrl(club);
  return (
    <button type="button" className="club-tile" onClick={() => onPick(club)}>
      <span className="club-tile-mark">
        {logo
          ? <img src={logo} alt="" loading="lazy" onError={() => setBroken(true)} />
          : <span className="club-tile-initials" aria-hidden="true">{initials(club.name)}</span>}
      </span>
      <span className="club-tile-name">{club.name}</span>
    </button>
  );
}

// Past this many a search box earns its place; below it, it is clutter.
const SEARCH_FROM = 9;

function ClubPicker({ clubs, onPick }) {
  const [query, setQuery] = React.useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? clubs.filter(c => c.name.toLowerCase().includes(q)) : clubs;

  return (
    <div className="club-picker">
      <h1 className="club-picker-title">Choose your club</h1>
      {clubs.length >= SEARCH_FROM && (
        <input className="w3-input club-picker-search" type="search" value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search for your club" aria-label="Search for your club" />
      )}
      {shown.length ? (
        <div className="club-picker-grid">
          {shown.map(c => <ClubTile key={c.id} club={c} onPick={onPick} />)}
        </div>
      ) : (
        <p className="club-picker-empty">No club matches "{query}".</p>
      )}
    </div>
  );
}

export default ClubPicker;
